import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useClusterStore } from '../../stores/clusterStore';
import { clusterShortName } from '../../lib/clusterName';

export function ClusterSelector() {
  const clusters = useClusterStore(s => s.clusters);
  const activeSlug = useClusterStore(s => s.activeSlug);
  const setActive = useClusterStore(s => s.setActive);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current =
    activeSlug === 'all'
      ? null
      : (clusters.find(c => c.slug === activeSlug) ?? null);

  const label =
    activeSlug === 'all'
      ? 'Todos'
      : current
        ? clusterShortName(current.name)
        : activeSlug;

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-sm text-text hover:bg-bg focus:outline-none focus:ring-2 focus:ring-accent/40"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="font-medium">{label}</span>
        <ChevronDown className="h-3.5 w-3.5 text-textMute" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1 w-56 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-md border border-border bg-surface shadow-md"
        >
          <button
            type="button"
            role="option"
            aria-selected={activeSlug === 'all'}
            onClick={() => {
              setActive('all');
              setOpen(false);
            }}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg"
          >
            <span>Todos</span>
            {activeSlug === 'all' && (
              <span aria-hidden className="h-2 w-2 rounded-full bg-okDot" />
            )}
          </button>
          <div className="border-t border-borderSoft" />
          {clusters.length === 0 ? (
            <div className="px-3 py-2 text-xs text-textMute">
              Nenhum cluster cadastrado
            </div>
          ) : (
            clusters.map(c => {
              const isActive = c.slug === activeSlug;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    setActive(c.slug);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg"
                >
                  <span className="text-text">{clusterShortName(c.name)}</span>
                  {isActive && (
                    <span
                      aria-hidden
                      className="h-2 w-2 rounded-full bg-okDot"
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
